import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const PipelineVisualization = ({ leads, onStageChange }) => {
  const [draggedLead, setDraggedLead] = useState(null);

  const stages = [
    { id: 'new', label: 'Nuevo', color: 'var(--color-accent)', icon: 'UserPlus' },
    { id: 'contacted', label: 'Contactado', color: '#3B82F6', icon: 'Phone' },
    { id: 'qualified', label: 'Calificado', color: '#8B5CF6', icon: 'CheckCircle' },
    { id: 'proposal', label: 'Propuesta', color: '#F59E0B', icon: 'FileText' },
    { id: 'negotiation', label: 'Negociación', color: '#10B981', icon: 'MessageSquare' },
    { id: 'closed_won', label: 'Ganado', color: 'var(--color-success)', icon: 'Trophy' }
  ];

  const getLeadsByStage = (stageId) => {
    return leads?.filter(lead => lead?.stage === stageId);
  };

  const handleDragStart = (e, lead) => {
    setDraggedLead(lead);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, stageId) => {
    e?.preventDefault();
    if (draggedLead && draggedLead?.stage !== stageId) {
      onStageChange(draggedLead, stageId);
    }
    setDraggedLead(null);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN',
      minimumFractionDigits: 0
    })?.format(amount);
  };

  const calculateStageValue = (stageId) => {
    const stageLeads = getLeadsByStage(stageId);
    return stageLeads?.reduce((sum, lead) => sum + lead?.presupuesto, 0);
  };

  return (
    <div className="bg-card rounded-xl p-4 md:p-6 border border-border">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Icon name="LayoutGrid" size={20} className="mr-2 text-primary" />
          <h3 className="text-lg font-semibold text-foreground font-heading">
            Pipeline de Ventas
          </h3>
        </div>
        <div className="text-sm text-muted-foreground font-caption">
          Arrastra para cambiar etapa
        </div>
      </div>
      {/* Desktop Pipeline View */}
      <div className="hidden lg:grid lg:grid-cols-6 gap-4">
        {stages?.map((stage) => {
          const stageLeads = getLeadsByStage(stage?.id);
          const stageValue = calculateStageValue(stage?.id);

          return (
            <div
              key={stage?.id}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, stage?.id)}
              className="bg-muted/30 rounded-xl p-3 min-h-[400px]"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center">
                  <div 
                    className="w-8 h-8 rounded-lg flex items-center justify-center mr-2"
                    style={{ backgroundColor: `${stage?.color}20` }}
                  >
                    <Icon name={stage?.icon} size={16} color={stage?.color} />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground">
                      {stage?.label}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {stageLeads?.length} leads
                    </div>
                  </div>
                </div>
              </div>
              <div className="text-xs font-medium text-foreground mb-3">
                {formatCurrency(stageValue)}
              </div>
              <div className="space-y-2">
                {stageLeads?.map((lead) => (
                  <div
                    key={lead?.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, lead)}
                    className="bg-card rounded-lg p-3 border border-border cursor-move hover:shadow-md transition-smooth"
                  >
                    <div className="font-medium text-sm text-foreground mb-1 truncate">
                      {lead?.nombre}
                    </div>
                    <div className="text-xs text-muted-foreground mb-2 truncate">
                      {lead?.proyectoInteres}
                    </div>
                    <div className="text-xs font-medium text-foreground">
                      {formatCurrency(lead?.presupuesto)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {/* Mobile Pipeline View */}
      <div className="lg:hidden space-y-4">
        {stages?.map((stage) => {
          const stageLeads = getLeadsByStage(stage?.id);
          const stageValue = calculateStageValue(stage?.id);

          return (
            <div key={stage?.id} className="bg-muted/30 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center">
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center mr-3"
                    style={{ backgroundColor: `${stage?.color}20` }}
                  >
                    <Icon name={stage?.icon} size={20} color={stage?.color} />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground">
                      {stage?.label}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {stageLeads?.length} leads • {formatCurrency(stageValue)}
                    </div>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                {stageLeads?.map((lead) => (
                  <div
                    key={lead?.id}
                    className="bg-card rounded-lg p-3 border border-border"
                  >
                    <div className="font-medium text-sm text-foreground mb-1">
                      {lead?.nombre}
                    </div>
                    <div className="text-xs text-muted-foreground mb-2">
                      {lead?.proyectoInteres}
                    </div>
                    <div className="text-xs font-medium text-foreground">
                      {formatCurrency(lead?.presupuesto)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PipelineVisualization;